import { navigate } from '@reach/router'
import cx from 'classnames'
import React from 'react'

import { Card, Price, ProductImage, Subtitle, Tags, Title } from '@cinch/oxd-components/product-card/src'
import tokens from '@cinch/oxd-utils/tokens/src'

import { removeMarketingParamsFromUrl } from '@cinch/oxd-utils/browser-support/src'
import Save from './components/save-button/save-button.component'

import { TagItem } from '@cinch/oxd-components/product-card/src/components/tags/tags.component'

import * as styles from './product-list-card.module.css'

export interface ProductListCardProps {
  id: string
  image: string
  price: number
  title: string
  subtitle: string
  hasData?: boolean
  isAvailable?: boolean
  isSaved: boolean
  isPending: boolean
  tags: TagItem[]
  unavailable?: string
  isUnfavourited?: boolean
  userLocation?: Pick<Cinch.UserProfileRequest, 'latitude' | 'longitude'>
  linkToProductView?: boolean
  isCinchStock?: boolean

  fetchData?: (userLocation?: Pick<Cinch.UserProfileRequest, 'latitude' | 'longitude'>) => void
  onSave?: (newVal: boolean) => void
}

const getPreviousURL = () => {
  if (typeof window !== 'undefined') {
    return window.location.pathname + window.location.search
  }
}

const { font } = tokens.customProperties

const numberWithCommas = (x: number) => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const ERROR_MESSAGE = 'Image Unavailable'

const ProductListCard: React.FC<ProductListCardProps> = ({
  id,
  price,
  image,
  title,
  subtitle,
  isSaved,
  isPending = false,
  isAvailable,
  onSave,
  tags,
  isUnfavourited,
  unavailable,
  linkToProductView = true,
  isCinchStock = false,
}) => {
  const shouldLink = linkToProductView && isAvailable
  const onClick = () => {
    const previousUrl = getPreviousURL()
    if (previousUrl && previousUrl.includes('cid')) {
      const newURL = removeMarketingParamsFromUrl(previousUrl)
      window.history.replaceState({}, 'replaceMarketingParams', newURL)
    }
    if (isCinchStock) {
      navigate(`/direct/vehicle/${id}`, { state: { previousUrl } })
    } else {
      navigate(`/vehicle/${id}`, { state: { previousUrl } })
    }
  }

  let containerProps = {}

  if (shouldLink) {
    containerProps = { role: 'link', onClick }
  }

  return (
    <div
      data-testid="product-list-card"
      className={cx(styles.container, {
        [styles.link]: shouldLink,
        [styles.unavailable]: isUnfavourited && isAvailable,
      })}
      {...containerProps}
    >
      <Card disabled={isUnfavourited} isHoverable={true}>
        <ProductImage
          alt={title}
          isAvailable={isAvailable}
          image={image}
          unavailableImageMessage={(!isAvailable && ' ') || unavailable || ERROR_MESSAGE}
          isPending={isPending}
        />
        <div className={cx(styles.meta, { [styles.unavailable]: !isAvailable })}>
          <div className={styles.actions}>
            {!!onSave && isAvailable && (
              <Save
                isPending={isPending || (!isAvailable && !isSaved)}
                className={styles.save}
                isSaved={isSaved}
                onClick={() => onSave(!isSaved)}
                size={font.size.l}
              />
            )}
          </div>
          <Title isPending={isPending}>{title}</Title>
          <Subtitle isPending={isPending}>{subtitle}</Subtitle>
          {isAvailable && <Price isPending={isPending}>{`£${numberWithCommas(price)}`}</Price>}
          <Tags isPending={isPending} items={tags} />
        </div>
      </Card>
    </div>
  )
}

ProductListCard.defaultProps = {
  hasData: true,
}

export default ProductListCard
